const SCROLL_VIDEO_SELECTOR = 'deferred-media[data-scroll-video]';
const ACTIVE_RATIO = 0.45;

/** @type {IntersectionObserver | null} */
let observer = null;

/**
 * @typedef {HTMLElement & {
 *   loadContent?: (hydrate?: boolean) => void,
 *   isPlaying?: boolean
 * }} ScrollVideoContainer
 */

/**
 * @param {ScrollVideoContainer} container
 */
function getVideo(container) {
  return container.querySelector('video');
}

/**
 * @param {ScrollVideoContainer} container
 */
function ensureVideo(container) {
  let video = getVideo(container);

  if (!video && !container.hasAttribute('data-media-loaded') && typeof container.loadContent === 'function') {
    container.loadContent(false);
    video = getVideo(container);
  }

  if (!video) return null;

  video.muted = true;
  video.playsInline = true;

  return video;
}

/**
 * @param {ScrollVideoContainer} container
 */
function playScrollVideo(container) {
  const video = ensureVideo(container);
  if (!video) return;

  video.play().catch(() => {});
  container.isPlaying = true;
}

/**
 * @param {ScrollVideoContainer} container
 */
function pauseScrollVideo(container) {
  const video = getVideo(container);
  if (!video) return;

  video.pause();
  container.isPlaying = false;
}

/**
 * @param {IntersectionObserverEntry[]} entries
 */
function handleVisibility(entries) {
  entries.forEach((entry) => {
    const container = entry.target;
    if (!(container instanceof HTMLElement)) return;

    const inView = entry.isIntersecting && entry.intersectionRatio >= ACTIVE_RATIO;

    container.dataset.inView = inView ? 'true' : 'false';

    if (document.hidden || !inView) {
      pauseScrollVideo(container);
      return;
    }

    playScrollVideo(container);
  });
}

function getObserver() {
  if (observer) return observer;

  observer = new IntersectionObserver(handleVisibility, {
    threshold: [0, ACTIVE_RATIO, 0.75],
    rootMargin: '-10% 0px -10% 0px',
  });

  return observer;
}

/**
 * @param {ParentNode} [root]
 */
function initScrollVideos(root = document) {
  root.querySelectorAll(SCROLL_VIDEO_SELECTOR).forEach((container) => {
    if (!(container instanceof HTMLElement)) return;

    if (container.dataset.scrollVideoInitialized === 'true') return;

    container.dataset.scrollVideoInitialized = 'true';
    // Hydrate only when visible: Horizon's loadContent broadcasts a playback
    // event that would otherwise pause an autoplay video elsewhere on startup.
    getObserver().observe(container);
  });
}

document.addEventListener('DOMContentLoaded', () => initScrollVideos());
document.addEventListener('shopify:section:load', (event) => {
  if (event.target instanceof Element) {
    initScrollVideos(event.target);
  }
});

document.addEventListener('visibilitychange', () => {
  document.querySelectorAll(SCROLL_VIDEO_SELECTOR).forEach((container) => {
    if (!(container instanceof HTMLElement)) return;

    if (document.hidden) {
      pauseScrollVideo(container);
    } else if (container.dataset.inView === 'true') {
      playScrollVideo(container);
    }
  });
});
